import { useEffect, useMemo, useState } from "react";
import "./AdminDashboard.css";
import AdminAddUserModal from "./AdminAddUserModal";
import { StatsGrid } from "../components/upgraded/StatsWidget";
import { buildAnalyticsStats } from "../components/upgraded/analyticsStats";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 10;

const tabs = [
  { key: "users", label: "students" },
  { key: "mindmap", label: "Mind Mapping" },
  { key: "ideaValidation", label: "Idea Validation Reports" }
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("users");
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  /* ============ Analytics (StatsGrid) ============ */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/admin/analytics`, { credentials: "include" });
        const json = await res.json();
        if (active && json?.success) setAnalytics(json.data);
      } catch (err) {
        console.error("Admin analytics error:", err);
      } finally {
        if (active) setStatsLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const statCards = useMemo(() => buildAnalyticsStats(analytics), [analytics]);

  /* ================= Fetch ================= */
  const fetchData = async (tab) => {
    setLoading(true);
    setData([]);
    setSearch("");
    setCurrentPage(1);

    const endpoints = {
      users: "/admin/users",
      mindmap: "/admin/mind-mapping",
      ideaValidation: "/admin/full/idea-validation"
    };

    try {
      const res = await fetch(`${BASE_URL}${endpoints[tab]}`);
      const json = await res.json();

      const keyMap = {
        users: json.users,
        mindmap: json.responses,
        ideaValidation: json.forms
      };

      setData(keyMap[tab] || []);
    } catch (err) {
      console.error("Admin fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  /* ================= Search ================= */
  const filteredData = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();

    return data.filter((row) =>
      Object.values(row).some(
        (val) => val && String(val).toLowerCase().includes(q)
      )
    );
  }, [search, data]);

  /* ================= Stats ================= */
  const last24HoursCount = useMemo(() => {
    const last24 = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return data.filter(
      (i) => i.created_at && new Date(i.created_at) >= last24
    ).length;
  }, [data]);

  /* ================= Pagination ================= */
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = `/Admin/login/${process.env.REACT_APP_ADMIN_PATH}/en-list/admin`;
  };

  return (
    <div className="admin-panel-wrapper">
      {/* Navbar */}
      <div className="admin-panel-navbar">
        <div className="admin-panel-navbar-title">Admin Dashboard</div>
        <button className="admin-panel-logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Analytics stat cards */}
      <div style={{ padding: "20px 24px 4px" }}>
        <StatsGrid stats={statCards.length ? statCards : undefined} loading={statsLoading} />
      </div>

      {/* Tabs */}
      <div className="admin-panel-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`admin-panel-tab-btn ${
              activeTab === tab.key ? "active" : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="admin-panel-search-wrapper">
        <input
          className="admin-panel-search"
          placeholder="Search anything..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Stats */}
      <div className="admin-panel-stats-row">
        <div className="admin-panel-stats-left">
          <div className="admin-panel-stat-box">
            <span className="stat-title">New (24 hrs)</span>
            <span className="stat-value">{last24HoursCount}</span>
          </div>
          <div className="admin-panel-stat-box">
            <span className="stat-title">Total</span>
            <span className="stat-value">{data.length}</span>
          </div>
        </div>

        {activeTab === "users" && (
          <button
            className="admin-panel-add-btn"
            onClick={() => setShowAddUserModal(true)}
          >
            + Add User
          </button>
        )}
      </div>

      {/* Table */}
      <div className="admin-panel-content">
        {loading && <p className="admin-panel-loading">Loading...</p>}

        {!loading && paginatedData.length > 0 && (
          <div className="admin-panel-table-scroll">
            <table className="admin-panel-table">
              <thead>
                <tr>
                  {Object.keys(paginatedData[0]).map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).map((val, j) => (
                      <td key={j}>
                        {Array.isArray(val)
                          ? val.join(", ")
                          : typeof val === "object" && val
                          ? JSON.stringify(val)
                          : String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && paginatedData.length === 0 && (
          <p className="admin-panel-empty">No records found</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-panel-pagination-bar">
          <span>
            {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} of{" "}
            {totalItems}
          </span>
          <div>
            {currentPage > 1 && (
              <button onClick={() => setCurrentPage(currentPage - 1)}>
                &lt;
              </button>
            )}
            <span className="admin-panel-pagination-current-page">
              {currentPage}
            </span>
            {currentPage < totalPages && (
              <button onClick={() => setCurrentPage(currentPage + 1)}>
                &gt;
              </button>
            )}
          </div>
        </div>
      )}

      {showAddUserModal && (
        <AdminAddUserModal
          onClose={() => setShowAddUserModal(false)}
          onSuccess={() => fetchData("users")}
        />
      )}
    </div>
  );
}
